<script setup>
import dayjs from 'dayjs'
import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxColorBox } from 'devextreme-vue/color-box'
import { useDataGridWidget, useProjectPresentationExport } from '@/composables/data-grid-toolbar'
import { useGridRef, useODataSource, useLookup, useDefaultEditing, useDefaultGridOptions, useDefaultExport, useUpdateMasterDetail, useRouteColumnFilter, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useArchivedAtColumn, useArchivedColumn, useListingColumn, useStartColumn, useEndColumn, useProjectTagsColumn, useCostTotalColumn, useMarginColumn, useMarginPercentageColumn, useTotalNoVatColumn, useTotalVatColumn, useBudgetDocumentColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useProjectDeleteOptions } from '@/composables/delete-options'

import { useChips } from '@/composables/chips'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'

import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TaskProgressSummary from '@/components/task/TaskProgressSummary.vue'

import BudgetDocumentPrintDialog from '@/components/accounting/BudgetDocumentPrintDialog.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'

import ProjectDeliveryNoteDialog from './ProjectDeliveryNoteDialog.vue'
import ProjectGridDetail from './ProjectGridDetail.vue'

const gridId = 'projects-grid-v3'

const i18n = getI18n()
const dataGridWidget = useDataGridWidget()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const accountingStore = useAccountingStore()
const editItem = ref(null)
const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }
const printItem = ref(null)
const printBudgetDocumentId = ref(null)
const pendingUrls = ref({})
const localizedTags = planningStore.getLocalizedProjectTags()

const financialColumnsVisible = webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)

const { gridRef } = useGridRef()
const { repaintRow } = useUpdateMasterDetail()
const onDetailRowSaved = (projectId) => repaintRow(gridRef, planningApi.getProjectByIdAsync, projectId, projectId)
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.project', 2) })
const { deleteOptions, openDeleteOptions, onDeleteOptionSelect } = useProjectDeleteOptions({
  onDeleted: async() => { await gridRef.value?.instance.refresh() },
})

const { archiveWarning, showArchived, archiveButton, archiveAsync, archiveDisplayExpr, archiveCellTemplate, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { await planningStore.archiveProjectAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteProjectAsync(id, config) },
  gridRef: gridRef,
  onDeleteBlocked: (item, error) => { openDeleteOptions(item, error) },
})
const editors = {}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.projectsUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})

planningStore.fetchProjectTagsAsync({ all: true }, true)
accountingStore.fetchBudgetDocumentExportTemplatesAsync({}, true)

const { getProjectTagChips } = useChips()

const { onContentReady: applyRouteFilter } = useRouteColumnFilter(gridRef, { projectId: 'id' })

const onContentReady = () => {
  const projectId = webSiteStore.editedProjectId
  if (!projectId) return applyRouteFilter()
  webSiteStore.editedProjectId = null
  showArchived.value = true
  gridRef.value.instance.columnOption('id', 'filterValue', projectId)
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      { name: 'edit', 
        visible: (e) => !e.row?.data?.archived, 
        onClick: (e) => { 
          editItem.value = { ...e.row.data } 
        },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '6rem',
  },
  {
    dataField: 'id',
    caption: i18n.t('common.id'),
    width: '3rem',
  },
  useStartColumn({ sortOrder: 'desc', validationRules: [{ type: 'required' }]}),
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    cellTemplate: 'name-cell-template',
    sortOrder: 'desc',
    validationRules: [{ type: 'required' }],
    width: '12rem',
  },
  useListingColumn({archiveDisplayExpr, archiveCellTemplate }),
  {
    dataField: 'status', caption: i18n.t('common.status'),
    lookup: useLookup({store: planningStore.getLocalizedProjectStatuses(), displayExpr: 'name' }),
    cellTemplate: 'status-cell-template',
  },
  useBudgetDocumentColumn({ visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible }),
  {
    dataField: 'task_progress',
    caption: i18n.t('common.tasks.tab'),
    width: '9rem',
    allowEditing: false,
    allowFiltering: false,
    allowSorting: false,
    calculateCellValue: (row) => (row.task_progress ? `${row.task_progress.done}/${row.task_progress.total}` : ''),
    cellTemplate: 'task-progress-cell-template',
  },
  { ...useTotalNoVatColumn(), allowEditing: false, visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible },
  { ...useTotalVatColumn(), allowEditing: false, visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible },
  { ...useCostTotalColumn(), allowEditing: false, visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible },
  { ...useMarginColumn(), allowEditing: false, visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible },
  { ...useMarginPercentageColumn(), allowEditing: false, visible: financialColumnsVisible, showInColumnChooser: financialColumnsVisible },
  {
    dataField: 'color', caption: i18n.t('common.color'),
    editCellTemplate: 'colorPicker',
    showInColumnChooser: false,
    setCellValue: (newData, value) => {
      newData.color = value
    },
    visible: false,
  },
  useEndColumn({ validationRules: [{ type: 'required' }]}),
  { dataField: 'description', caption: i18n.t('common.description'), width: '12rem' },
  {
    dataField: 'currency_ref',
    caption: i18n.t('common.currency'),
    lookup: useLookup({ dataSource: webSiteStore.getLocalizedCurrencies(), valueExpr: 'id', displayExpr: 'name' }),
  },
  useProjectTagsColumn({
    dataSource: localizedTags,
    cellTemplate: 'project-tags-cell-template',
  }),
  {
    dataField: 'project_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate:'fileViewer',
    editCellTemplate: 'fileUploader',
    width: '16rem',
    allowExporting: false,
  },
  useArchivedAtColumn(),
  useArchivedColumn(),
]

const defaultEnd = (value) => {
  return dayjs(`${dayjs(value).format('YYYY-MM-DD')}T${webSiteStore.getDefaultEndTime()}`).toISOString()
}

const onInitNewRow = (e) => {
  e.data.status = 0
  e.data.start = dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultStartTime()}`).toISOString()
  e.data.end = defaultEnd(e.data.start)
}

const alwaysEditableColumns = ['name', 'description', 'status', 'tags', 'project_files']

const onEditorPreparing = async(e) => {
  if (e.parentType === 'dataRow') {
    const fieldName = e.dataField
    e.editorOptions.onInitialized = (e) => { editors[fieldName] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'start') {
    const defaultOnValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (e) => {
      defaultOnValueChanged(e)
      if (editors['end']) {
        editors['end'].option('min', e.value?.toISOString())
        editors['end'].option('value', defaultEnd(e.value))
      }
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'end') {
    e.editorOptions.onInitialized = (args) => {
      editors[e.dataField] = args.component
      if (e.row.data?.start) {
        args.component.option('min', e.row.data.start)
      }
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'name' && e.row?.data?.id) {
    e.promise = planningStore.fetchProjectByIdAsync(e.row.data?.id).then((project) => {
      for (const editor in editors) {
        if (project.data?.has_listing_items && !alwaysEditableColumns.includes(editor)) {
          editors[editor].option('readOnly', true)
        }
      }
    })
  }
}

const onRowUpdating = async(e) => {
  if (!e.newData.project_files) {
    e.newData.project_files = e.oldData.project_files || []
  }
  if (pendingUrls.value[e.key] !== undefined) {
    e.newData.project_urls = pendingUrls.value[e.key]
    delete pendingUrls.value[e.key]
  } else {
    e.newData.project_urls = e.oldData.project_urls || []
  }
}

const printTypeEnum = Object.freeze({
  QUOTE: 0,
  DELIVERY_NOTE: 1,
})

const getPrintItems = (hasBudgetDocument = false) => ([
  { key: printTypeEnum.QUOTE, name: i18n.t('common.quote'), icon: 'mdi mdi-file-document-outline', disabled: !hasBudgetDocument },
  { key: printTypeEnum.DELIVERY_NOTE, name: i18n.t('common.deliveryNote'), icon: 'mdi mdi-truck-outline' },
])

const onSelectionChanged = (e) => {
  dataGridWidget.printWidgetDropDown.value.option('disabled', e.selectedRowsData.length !== 1)
  dataGridWidget.printWidgetDropDown.value.option('items', getPrintItems(!!e.selectedRowsData[0]?.budget_document?.id))
  dataGridWidget.createInvoiceWidgetButton.value?.option('disabled', !e.selectedRowsData.length || e.selectedRowsData.some(project => project.archived))
}

const invoicedProjectIds = ref([])

const onInvoiceCreated = () => {
  invoicedProjectIds.value = []
}

const onPrintItemClick = (e) => {
  const rows = gridRef.value.instance.getSelectedRowsData()
  if (rows.length !== 1) return
  const project = { ...rows[0] }
  if (e.itemData.key === printTypeEnum.QUOTE) {
    printBudgetDocumentId.value = project.budget_document.id
  } else {
    printItem.value = project
  }
}


const gridOptions = useDefaultGridOptions({
  id: gridId,
  dateSerializationFormat: 'yyyy-MM-ddTHH:mm:ssZ',
  dataSource: gridDataSource,
  columns: columns,
  editing: useDefaultEditing(),
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  export: exportOptions,
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onRowPrepared: onArchiveRowPrepared,
  presentationExports: [useProjectPresentationExport(i18n.t('common.project'))],
  exportWidgets: [
    dataGridWidget.printDropDownWidget({ items: getPrintItems(), onItemClick: onPrintItemClick }),
    ...(financialColumnsVisible ? [dataGridWidget.createInvoiceWidget({ onClick: () => { invoicedProjectIds.value = gridRef.value.instance.getSelectedRowKeys() } })] : []),
  ],
  onRowRemoving: onRowRemoving,
  onContentReady: onContentReady,
  onRowUpdating: onRowUpdating,
  onExporting: onExporting,
  onSelectionChanged: onSelectionChanged,
  clearFilterButton: true,
  archivedToggle: true,
  addButtons: [{
    icon: 'mdi-flash',
    hint: i18n.t('common.quickCreate'),
    onClick: () => onAddClick(true),
  }],
  addButton: { onClick: () => onAddClick(false) },
})

const onDownloadProjectFileAsync = async(e) => {
  if (e) {
    await planningStore.downloadProjectFileAsync(e.id, e.name)
  }
}

const onColorChanged = (e, templateOptions) => {
  templateOptions.setValue(e.value)
}

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  await planningStore.fetchProjectTagsAsync({ all: true }, true)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}


useEntityChanges([entityChangeTypeEnum.PROJECT], (change) => refreshChangedRowAsync(gridRef, change, planningApi.getProjectByIdAsync), () => editItem.value !== null)
</script>
<template>
  <div>
    <q-dialog :model-value="printBudgetDocumentId !== null" @hide="printBudgetDocumentId = null">
      <budget-document-print-dialog
        v-if="printBudgetDocumentId !== null"
        :budget-document-id="printBudgetDocumentId"
        @cancel="printBudgetDocumentId = null"
      />
    </q-dialog>
    <q-dialog :model-value="printItem !== null" class="dialog-md" @hide="printItem = null">
      <project-delivery-note-dialog
        v-if="printItem !== null"
        :model-value="printItem"
        @cancel="printItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="invoicedProjectIds.length > 0" persistent class="dialog-xl">
      <invoice-edit
        v-if="invoicedProjectIds.length > 0"
        :project-ids="invoicedProjectIds"
        @update="onInvoiceCreated"
        @cancel="invoicedProjectIds = []"
      />
    </q-dialog>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <project-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :initial-quick-create="quickCreate"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #name-cell-template="{ data: templateOptions }">
        <span
          :style="{ 'border-left': `4px solid ${templateOptions.data.color}`, 'padding-left': '.25rem' }"
        >{{ templateOptions.data.name }}</span>
      </template>
      <template #master-detail="{ data: templateData }">
        <project-grid-detail
          :data="templateData"
          @row-saved="onDetailRowSaved"
        />
      </template>
      <template #project-tags-cell-template="{ data: templateOptions }">
        <chip-template :values="getProjectTagChips(templateOptions.data.tag_ids)" />
      </template>
      <template #task-progress-cell-template="{ data: templateOptions }">
        <task-progress-summary v-if="templateOptions.data.task_progress" :progress="templateOptions.data.task_progress" />
      </template>
      <template #status-cell-template="{ data: templateOptions }">
        <chip-template :values="[planningStore.getLocalizedProjectStatuses()[templateOptions.data.status]]" />
      </template>
      <template #fileUploader="{ data: templateOptions }">
        <file-url-edit
          :cell-info="templateOptions"
          :urls="templateOptions.data.project_urls"
          @download="onDownloadProjectFileAsync"
          @update:urls="pendingUrls[templateOptions.key] = $event"
        />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
          :urls="templateOptions.data.project_urls"
        />
      </template>
      <template #colorPicker="{ data: templateOptions }">
        <dx-color-box
          :value="templateOptions.data.color"
          @value-changed="(e) => onColorChanged(e, templateOptions)"
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
